import { useEffect, useRef } from 'react'
import { selectSettingsLoaded } from '@/chat-common/store/chat-ux.selectors'
import { getJurisdictionLabel, inferJurisdictionKind } from '@/jurisdiction-mappings/jurisdiction-maps'
import type { MatterRead } from '@/matter/store/schemas'
import { canEditMatter } from '@/matter/utils/matter-permissions'
import { useAppSelector } from '@/store/store-hooks'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { matterCreatedBeforeLaunch } from '@/analytics/utils/matter-jurisdiction-analytics'

/**
 * Jurisdiction display/eligibility state for MatterDetailsJurisdiction.
 *
 * CTA-impression dedup is keyed to `matter.id` via a ref that lives for the caller's mount lifetime,
 * so a caller that stays mounted across matter navigation (the context rail) dedupes per matter shown.
 */
export function useMatterJurisdiction(matter: MatterRead) {
  const settingsLoaded = useAppSelector(selectSettingsLoaded)
  const { trackEvent } = useAnalytics()
  const jurisdictionLabel = matter.default_jurisdiction ? getJurisdictionLabel(matter.default_jurisdiction) : null
  const jurisdictionKind = inferJurisdictionKind(matter.default_jurisdiction)
  const showMissingJurisdiction = settingsLoaded && !jurisdictionLabel
  const canEdit = canEditMatter(matter)

  const ctaViewedMatterIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!showMissingJurisdiction || !canEdit) return
    if (ctaViewedMatterIdRef.current === matter.id) return
    ctaViewedMatterIdRef.current = matter.id
    trackEvent(AnalyticsEvent.MatterJurisdictionCtaViewed, {
      matterId: matter.id,
      matterCreatedBeforeLaunch: matterCreatedBeforeLaunch(matter.created_at),
    })
  }, [matter.id, matter.created_at, showMissingJurisdiction, canEdit, trackEvent])

  const trackCtaClicked = () => {
    trackEvent(AnalyticsEvent.MatterJurisdictionCtaClicked, {
      matterId: matter.id,
      matterCreatedBeforeLaunch: matterCreatedBeforeLaunch(matter.created_at),
    })
  }

  return { jurisdictionLabel, jurisdictionKind, showMissingJurisdiction, canEdit, trackCtaClicked }
}
